import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { APP_LOGO_URL } from "../../config/branding";
import NotificationBell from "../../components/NotificationBell";
import {
  FaHome,
  FaBook,
  FaUsers,
  FaServicestack,
  FaCog,
  FaBars,
  FaSignOutAlt,
  FaGift,
  FaClipboardList,
  FaStar,
  FaImages,
  FaBell,
} from "react-icons/fa";
import axios from "axios";
import LogoutConfirmModal from "../../components/LogoutConfirmModal";
import "./shopStyles.css";

function SidebarLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [canShowBoothRentals, setCanShowBoothRentals] = useState(false);

  const SIDEBAR_COLLAPSE_KEY = "ogestyle_sidebar_collapsed_shop";

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    setIsLoggedIn(!!token);

    // Get current role (R1=owner, R2=admin, R3=professional)
    const role =
      localStorage.getItem("ogestyle_role_override") ||
      localStorage.getItem("ogestyle_role");
    setCurrentRole(role);

    const savedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSE_KEY);
    if (savedCollapsed === "1") {
      setCollapsed(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const handleLogout = () => {
    localStorage.removeItem("ogestyle_token");
    localStorage.removeItem("ogestyle_role");
    localStorage.removeItem("ogestyle_roles");
    localStorage.removeItem("ogestyle_role_override");
    setIsLoggedIn(false);
    window.location.href = "/login";
  };

  // Detect current context by path (no structural changes)
  const path = window.location.pathname;
  const isPersonalContext =
    path.startsWith("/dashboard/maestro") || path.startsWith("/hairstylist");
  const isBusinessContext = path.startsWith("/dashboard") && !isPersonalContext;
  const isR2 = currentRole === "R2";

  useEffect(() => {
    let isMounted = true;

    const resolveBoothRentalsVisibility = async () => {
      if (isMounted) setCanShowBoothRentals(false);

      if (!isBusinessContext || isR2) {
        if (isMounted) setCanShowBoothRentals(false);
        return;
      }

      if (currentRole !== "R1") {
        if (isMounted) setCanShowBoothRentals(true);
        return;
      }

      try {
        const token = localStorage.getItem("ogestyle_token");
        if (!token) {
          if (isMounted) setCanShowBoothRentals(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/shop/my-shops`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const userShops = Array.isArray(response.data)
          ? response.data
          : response.data?.shops || [];

        const hasBoothRentalEnabledLocation = userShops.some(
          (shop) =>
            shop?.locationMode === "hybrid" ||
            shop?.locationMode === "booth_rental_only",
        );

        // U1: also respect the business-level "Allow booth rentals" toggle
        // (boothRentalSettings.allowBoothRentals). When it is explicitly
        // unchecked, hide Booth Rentals even if a location defaults to hybrid.
        let businessAllowsBoothRentals = true;
        const businessId = userShops[0]?.businessId;
        if (businessId) {
          try {
            const businessRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/businesses/${businessId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const biz = businessRes.data;
            businessAllowsBoothRentals =
              biz?.boothRentalSettings?.allowBoothRentals !== false;
          } catch (_bizErr) {
            // If the business flag can't be confirmed, fall back to location mode.
            businessAllowsBoothRentals = true;
          }
        }

        if (isMounted) {
          setCanShowBoothRentals(
            businessAllowsBoothRentals && hasBoothRentalEnabledLocation,
          );
        }
      } catch (_error) {
        if (isMounted) {
          // Fail-closed for owner safety: don't show Booth Rentals if mode state can't be confirmed.
          setCanShowBoothRentals(false);
        }
      }
    };

    resolveBoothRentalsVisibility();

    const refreshListener = () => {
      resolveBoothRentalsVisibility();
    };
    window.addEventListener(
      "ogestyle:refresh-booth-rentals-visibility",
      refreshListener,
    );

    return () => {
      isMounted = false;
      window.removeEventListener(
        "ogestyle:refresh-booth-rentals-visibility",
        refreshListener,
      );
    };
  }, [currentRole, isBusinessContext, isR2]);

  // Read roles to decide switch target
  const rolesList = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
  const canOwner = rolesList.includes("owner");
  const canAdmin = rolesList.includes("shop_admin");
  const canSwitchToProfessional =
    rolesList.includes("professional") ||
    rolesList.includes("R3") ||
    rolesList.includes("R4"); // Professional or Freelancer

  let switchLabel = "";
  let targetRoleCode = "";
  if (isBusinessContext) {
    if (canSwitchToProfessional) {
      switchLabel = "Personal Account";
      targetRoleCode = "R3";
    }
  } else if (isPersonalContext) {
    if (canOwner) {
      switchLabel = "Business Account";
      targetRoleCode = "R1";
    } else if (canAdmin) {
      switchLabel = "Business Account";
      targetRoleCode = "R2";
    }
  }

  // Role → path mapping (hard redirect to avoid layout shifts)
  const roleMap = {
    R1: "/dashboard",
    R2: "/dashboard",
    R3: "/dashboard/professional", // Unified professional route
    R5: "/",
    R6: "/portal-admin",
  };

  const handleSwitchClick = async (e) => {
    e.preventDefault();
    try {
      if (!targetRoleCode) return;

      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/switch-role`,
        { targetRoleCode },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { token: newToken, role } = res.data;
      localStorage.setItem("ogestyle_token", newToken);
      localStorage.setItem("ogestyle_role", role);
      localStorage.setItem("ogestyle_role_override", role);

      const next = roleMap[role] || "/";
      window.location.href = next;
    } catch (err) {
      // Keep UI minimal—no new classes
      alert(err?.response?.data?.message || "Unable to switch account.");
    }
  };

  return (
    <div className="dashboard-layout theme-typography">
      {/* Sidebar for Desktop */}
      <aside
        className={`dashboard-sidebar${collapsed ? " collapsed" : ""}${mobileMenuOpen ? " mobile-open" : ""}`}
      >
        <div className="sidebar-logo">
          <img src={APP_LOGO_URL} alt="OGESTYLE Logo" />
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* The owner had no bell at all, while the shop's own notices were being written to their account. */}
            {!collapsed && <NotificationBell viewAllPath="/dashboard/notifications" />}
            <button
              className="sidebar-toggle"
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <FaBars />
            </button>
          </div>
        </div>

        <ul
          onClick={(event) => {
            if (window.innerWidth <= 900 && event.target.closest("a")) {
              setMobileMenuOpen(false);
            }
          }}
        >
          <li className="menu-item">
            <Link to="/dashboard">
              <span className="menu-icon">
                <FaHome />
              </span>
              <span className="menu-text">Dashboard Home</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/bookings">
              <span className="menu-icon">
                <FaBook />
              </span>
              <span className="menu-text">Bookings</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/professionals">
              <span className="menu-icon">
                <FaUsers />
              </span>
              <span className="menu-text">Professionals</span>
            </Link>
          </li>
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/managers">
                <span className="menu-icon">🧭</span>
                <span className="menu-text">Managers</span>
              </Link>
            </li>
          )}
          {!isR2 && canShowBoothRentals && (
            <li className="menu-item">
              <Link to="/dashboard/booth-rentals">
                <span className="menu-icon">💼</span>
                <span className="menu-text">Booth Rentals</span>
              </Link>
            </li>
          )}
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/time-off-requests">
                <span className="menu-icon">⏰</span>
                <span className="menu-text">Time-Off Requests</span>
              </Link>
            </li>
          )}
          {/* Service Requests - Hidden */}
          {/* <li className="menu-item">
            <Link to="/dashboard/service-requests">
              <span className="menu-icon">📝</span>
              <span className="menu-text">Service Requests</span>
            </Link>
          </li> */}
          <li className="menu-item">
            <Link to="/dashboard/customers">
              <span className="menu-icon">👥</span>
              <span className="menu-text">Customers</span>
            </Link>
          </li>
          {/* MULTI-LOCATION: Temporarily hidden - See SESSION_SUMMARY_DEC_23_2025.md
          <li className="menu-item">
            <Link to="/dashboard/multi-location-requests">
              <span className="menu-icon">🗺️</span>
              <span className="menu-text">Multi-Location</span>
            </Link>
          </li>
          */}
          <li className="menu-item">
            <Link to="/dashboard/services">
              <span className="menu-icon">
                <FaServicestack />
              </span>
              <span className="menu-text">Services</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/products">
              <span className="menu-icon">📦</span>
              <span className="menu-text">Products</span>
            </Link>
          </li>
          {/* Service Inventory route revealed (2026-09-27). `/dashboard/inventory`
              has existed in App.js for R1/R2 all along with no menu entry pointing at
              it, which is why nobody could tell whether the feature was built. The
              page now says what it is instead of redirecting away. */}
          <li className="menu-item">
            <Link to="/dashboard/inventory">
              <span className="menu-icon">🧴</span>
              <span className="menu-text">Service Inventory</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/orders">
              <span className="menu-icon">🛍️</span>
              <span className="menu-text">Orders</span>
            </Link>
          </li>
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/locations">
                <span className="menu-icon">📍</span>
                <span className="menu-text">Locations</span>
              </Link>
            </li>
          )}
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/analytics">
                <span className="menu-icon">📊</span>
                <span className="menu-text">Analytics</span>
              </Link>
            </li>
          )}
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/payouts">
                <span className="menu-icon">💰</span>
                <span className="menu-text">Payouts</span>
              </Link>
            </li>
          )}
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/marketing">
                <span className="menu-icon">📨</span>
                <span className="menu-text">Marketing</span>
              </Link>
            </li>
          )}
          {!isR2 && (
            <li className="menu-item">
              <Link to="/dashboard/referrals">
                <span className="menu-icon">🤝</span>
                <span className="menu-text">Refer &amp; earn</span>
              </Link>
            </li>
          )}
          {/* RELEASED (Phase 14, 2026-10-21). This entry used to carry a "TEMPORARILY ENABLED FOR LOCAL REVIEW
              — do NOT commit or push to demo" guard, written when the package payout model was genuinely
              unimplemented. It is implemented now and was reconciled against the ledger on 2026-10-21: the shop
              is paid its lump sum at the FIRST completed session (D14) and the delivering professional a
              tranche per completed session (D15/D19), attributed by the service delivered. The guard is gone
              because leaving it would tell the next reader the money rail is missing when it is not.

              VISIBLE TO R2, deliberately. R2 may VIEW packages; they simply cannot create, edit or delete
              one. CRUD is owner-only (R1) and the server enforces that with authorizeRoles — the menu is
              not the guard. Narrowed 2026-10-03 from "hide it from R2", which was wrong: it removed a
              view-only screen R2 is allowed to use. */}
          <li className="menu-item">
            <Link to="/dashboard/packages">
              <span className="menu-icon">
                <FaGift />
              </span>
              <span className="menu-text">Packages</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/reviews">
              <span className="menu-icon">
                <FaStar />
              </span>
              <span className="menu-text">Reviews</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/waitlist">
              <span className="menu-icon">
                <FaClipboardList />
              </span>
              <span className="menu-text">Waitlist</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/looks">
              <span className="menu-icon">
                <FaImages />
              </span>
              <span className="menu-text">Looks</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/notifications">
              <span className="menu-icon">
                <FaBell />
              </span>
              <span className="menu-text">Notifications</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to="/dashboard/settings">
              <span className="menu-icon">
                <FaCog />
              </span>
              <span className="menu-text">Settings</span>
            </Link>
          </li>

          {/* NEW: Switch dashboard as a normal menu item using the same structure */}
          {switchLabel && (
            <li className="menu-item">
              <Link to="#" onClick={handleSwitchClick}>
                <span className="menu-icon">
                  <FaCog />
                </span>
                <span className="menu-text">{switchLabel}</span>
              </Link>
            </li>
          )}
        </ul>

        <button
          className="logout-button"
          onClick={() =>
            isLoggedIn
              ? setShowLogoutModal(true)
              : (window.location.href = "/login")
          }
        >
          <span className="menu-icon">
            <FaSignOutAlt />
          </span>
          <span className="menu-text">{isLoggedIn ? "Logout" : "Login"}</span>
        </button>
      </aside>

      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="mobile-sidebar-topbar">
        <button
          className="mobile-sidebar-toggle"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Open menu"
        >
          <FaBars />
        </button>
      </div>

      {/* Main Content */}
      <main className="dashboard-main">{children}</main>

      {/* Bottom Navigation for Mobile */}
      <nav className="bottom-nav">
        <Link to="/dashboard">
          <span className="nav-icon">
            <FaHome />
          </span>
          <span>Home</span>
        </Link>
        <Link to="/dashboard/bookings">
          <span className="nav-icon">
            <FaBook />
          </span>
          <span>Bookings</span>
        </Link>
        <Link to="/dashboard/professionals">
          <span className="nav-icon">
            <FaUsers />
          </span>
          <span>Pros</span>
        </Link>
        <Link to="/dashboard/services">
          <span className="nav-icon">
            <FaServicestack />
          </span>
          <span>Services</span>
        </Link>
        <Link to="/dashboard/products">
          <span className="nav-icon">📦</span>
          <span>Products</span>
        </Link>
        <Link to="/dashboard/orders">
          <span className="nav-icon">🛍️</span>
          <span>Orders</span>
        </Link>

        {/* More Menu Button */}
        <button
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "#00509d",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "0.75rem",
            fontWeight: "486",
            minWidth: "70px",
            position: "relative",
          }}
        >
          <span className="nav-icon">
            <FaBars />
          </span>
          <span>More</span>
        </button>
      </nav>

      {/* More Menu Overlay */}
      {showMoreMenu && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 998,
            }}
            onClick={() => setShowMoreMenu(false)}
          />
          <div
            style={{
              position: "fixed",
              bottom: "60px",
              left: 0,
              right: 0,
              background: "white",
              borderTop: "1px solid #e5e7eb",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.15)",
              zIndex: 999,
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <div style={{ padding: "16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  paddingBottom: "12px",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                  Menu
                </h3>
                <button
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "0",
                  }}
                >
                  ×
                </button>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {/* MULTI-LOCATION: Temporarily hidden - See SESSION_SUMMARY_DEC_23_2025.md
                <li style={{ marginBottom: '12px' }}>
                  <Link 
                    to="/dashboard/multi-location-requests" 
                    onClick={() => setShowMoreMenu(false)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '12px',
                      borderRadius: '8px',
                      background: '#f9fafb',
                      textDecoration: 'none',
                      color: '#374151',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>🗺️</span>
                    <span>Multi-Location Requests</span>
                  </Link>
                </li>
                */}
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/managers"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>🧭</span>
                      <span>Managers</span>
                    </Link>
                  </li>
                )}
                {!isR2 && canShowBoothRentals && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/booth-rentals"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>💼</span>
                      <span>Booth Rentals</span>
                    </Link>
                  </li>
                )}
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/time-off-requests"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>⏰</span>
                      <span>Time-Off Requests</span>
                    </Link>
                  </li>
                )}
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/analytics"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>📊</span>
                      <span>Analytics</span>
                    </Link>
                  </li>
                )}
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/payouts"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>💰</span>
                      <span>Payouts</span>
                    </Link>
                  </li>
                )}
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/referrals"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>🤝</span>
                      <span>Refer &amp; earn</span>
                    </Link>
                  </li>
                )}
                {/* Mobile copy of the released entry above — same state, same visibility. */}
                {/* Visible to R2 — view-only, same as the desktop entry above. */}
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/packages"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaGift />
                    <span>Packages</span>
                  </Link>
                </li>
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/customers"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <span style={{ fontSize: "18px" }}>👥</span>
                    <span>Customers</span>
                  </Link>
                </li>
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/waitlist"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaClipboardList />
                    <span>Waitlist</span>
                  </Link>
                </li>
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/looks"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaImages />
                    <span>Looks</span>
                  </Link>
                </li>
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/marketing"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>📨</span>
                      <span>Marketing</span>
                    </Link>
                  </li>
                )}
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/reviews"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaStar />
                    <span>Reviews</span>
                  </Link>
                </li>
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/notifications"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaBell />
                    <span>Notifications</span>
                  </Link>
                </li>
                {!isR2 && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="/dashboard/locations"
                      onClick={() => setShowMoreMenu(false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <span style={{ fontSize: "18px" }}>📍</span>
                      <span>Locations</span>
                    </Link>
                  </li>
                )}
                <li style={{ marginBottom: "12px" }}>
                  <Link
                    to="/dashboard/settings"
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      textDecoration: "none",
                      color: "#374151",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaCog />
                    <span>Settings</span>
                  </Link>
                </li>
                {switchLabel && (
                  <li style={{ marginBottom: "12px" }}>
                    <Link
                      to="#"
                      onClick={(e) => {
                        handleSwitchClick(e);
                        setShowMoreMenu(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        textDecoration: "none",
                        color: "#374151",
                        fontSize: "15px",
                        fontWeight: "500",
                      }}
                    >
                      <FaCog />
                      <span>{switchLabel}</span>
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      isLoggedIn
                        ? setShowLogoutModal(true)
                        : (window.location.href = "/login");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      borderRadius: "8px",
                      background: "#fef2f2",
                      border: "none",
                      width: "100%",
                      cursor: "pointer",
                      color: "#dc2626",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    <FaSignOutAlt />
                    <span>{isLoggedIn ? "Logout" : "Login"}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default SidebarLayout;
